/**
 * API Client fuer Image Intelligence (Packshot-Klassifikation)
 *
 * Endpoints fuer Batch-Analyse, Packshot-Zuweisung und Kontingent.
 */

import apiClient from '../api-client'

const BASE = '/api/v2/readiness'

// =============================================================================
// TYPES
// =============================================================================

export interface AnalysisEstimate {
  total_images: number
  unique_images: number
  already_analyzed: number
  to_analyze: number
  estimated_credits: number
  quota_remaining: number
  quota_sufficient: boolean
}

export interface AnalysisRun {
  id?: string
  run_id?: string
  status: string
  message?: string
  progress_current?: number
  progress_total?: number
  progress_percent?: number
  started_at?: string
  completed_at?: string
  estimated_remaining_seconds?: number
  results_summary?: Record<string, number>
  last_analyzed?: {
    product_name: string
    score: number
    category: string
  }
  credits_used?: number
  quota_used?: number
}

export interface AnalysisQuota {
  used: number
  limit: number
  remaining: number
  month: string
}

export interface PackshotCandidate {
  asset_id: string
  thumbnail_url: string
  overall_score: number
  category: string
  recommendation?: string
  scores?: Record<string, number>
}

export interface ProductPackshotStatus {
  status: 'no_images' | 'candidate' | 'no_candidate' | 'assigned' | 'not_analyzed'
  has_packshot_assigned: boolean
  best_candidate: PackshotCandidate | null
  all_analyses?: PackshotCandidate[]
}

export interface ImageIntelligenceCounts {
  no_images_count: number
  candidate_count: number
  no_good_candidate_count: number
  packshot_assigned_count: number
  not_analyzed_count: number
  last_analysis_run?: {
    run_id: string
    completed_at: string
    model_used: string
  }
}

export interface BulkSetResult {
  requested: number
  candidates_found: number
  assigned: number
}

// =============================================================================
// ANALYSIS ENDPOINTS
// =============================================================================

export async function getAnalysisEstimate(
  scope?: string,
  productIds?: string[]
): Promise<AnalysisEstimate> {
  const params: Record<string, string> = {}
  if (scope) params.scope = scope
  if (productIds?.length) params.product_ids = productIds.join(',')
  const response = await apiClient.get(`${BASE}/analyze/estimate`, { params })
  return response.data
}

export async function startAnalysis(
  scope: string = 'all',
  autoAssignThreshold?: number,
  productIds?: string[]
): Promise<AnalysisRun> {
  const body: Record<string, unknown> = { scope }
  if (autoAssignThreshold !== undefined) body.auto_assign_threshold = autoAssignThreshold
  if (productIds?.length) body.product_ids = productIds
  const response = await apiClient.post(`${BASE}/analyze`, body)
  return response.data
}

export async function getAnalysisStatus(runId: string): Promise<AnalysisRun> {
  const response = await apiClient.get(`${BASE}/analyze/${runId}/status`)
  return response.data
}

export async function cancelAnalysis(runId: string): Promise<AnalysisRun> {
  const response = await apiClient.post(`${BASE}/analyze/${runId}/cancel`)
  return response.data
}

export async function getAnalysisQuota(): Promise<AnalysisQuota> {
  const response = await apiClient.get(`${BASE}/analyze/quota`)
  return response.data
}

// =============================================================================
// PACKSHOT STATUS ENDPOINT
// =============================================================================

export async function getPackshotStatuses(
  productIds: string[],
  channelId?: string
): Promise<Record<string, ProductPackshotStatus>> {
  if (!productIds.length) return {}
  const params: Record<string, string> = { product_ids: productIds.join(',') }
  if (channelId) params.channel_id = channelId
  const response = await apiClient.get(`${BASE}/packshot-statuses`, { params })
  return response.data
}

// =============================================================================
// PACKSHOT ENDPOINTS
// =============================================================================

export async function setPackshot(
  productId: string,
  assetId: string,
  slot: number = 1,
  channelId?: string
): Promise<Record<string, unknown>> {
  const body: Record<string, unknown> = {
    product_id: productId,
    asset_id: assetId,
    slot,
  }
  if (channelId) body.channel_id = channelId
  const response = await apiClient.post(`${BASE}/set-packshot`, body)
  return response.data
}

export async function bulkSetPackshot(
  productIds: string[],
  minScore: number = 70,
  slot: number = 1,
  channelId?: string
): Promise<BulkSetResult> {
  const body: Record<string, unknown> = {
    product_ids: productIds,
    min_score: minScore,
    slot,
  }
  if (channelId) body.channel_id = channelId
  const response = await apiClient.post(`${BASE}/bulk-set-packshot`, body)
  return response.data
}
